import { Sparkles, MessageSquare, AlertTriangle, AlertCircle, Info, ThumbsDown } from "lucide-react";
import { useChatUI } from "@contexts/ChatContext";
import { useRejectedObservations } from "@features/chat/hooks/useRejectedObservations";

const SEVERITY_META = {
  risk: { icon: AlertTriangle, color: "#ef4444", bg: "rgba(239,68,68,0.12)", label: "Risk" },
  warn: { icon: AlertCircle, color: "#f59e0b", bg: "rgba(245,158,11,0.14)", label: "Warn" },
  info: { icon: Info, color: "#3b82f6", bg: "rgba(59,130,246,0.12)", label: "Info" },
};

/**
 * Compact inline render of a single AIObservation — used alongside
 * existing domain recommendations (e.g., cost savings) in expandable
 * table rows. Tapping "Discuss" opens the chat drawer with this
 * observation set as the current topic (shown as a banner in the
 * thread). Input stays empty so the user types their question.
 */
export default function AIObservationInlineCard({ observation, entityLabel }) {
  const { seedAndOpen } = useChatUI();
  const { reject } = useRejectedObservations();
  const severity = SEVERITY_META[observation.severity] ?? SEVERITY_META.info;
  const Icon = severity.icon;

  const onDiscuss = () => {
    seedAndOpen({
      topic: {
        title: observation.title,
        body: observation.body,
        containerLabel: entityLabel,
        severity: observation.severity,
        category: observation.category,
      },
    });
  };

  const onReject = () => {
    reject(observation).catch((err) =>
      console.error("[AIObservationInlineCard] reject failed", err)
    );
  };

  return (
    <div
      className="flex items-start gap-3 p-2.5 rounded-lg"
      style={{ background: "var(--bg-row-hover)", border: "1px solid var(--border-row)" }}
    >
      <div
        className="p-1 rounded flex-shrink-0 mt-0.5"
        style={{ background: severity.bg, color: severity.color }}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className="text-xs font-semibold" style={{ color: "var(--text-heading)" }}>
            {observation.title}
          </p>
          <span
            className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
            style={{ background: severity.bg, color: severity.color }}
          >
            <Sparkles className="w-2.5 h-2.5 inline mr-0.5" style={{ color: "var(--ai-icon)" }} />
            AI · {severity.label}
          </span>
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--text-body)" }}>
          {observation.body}
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={onDiscuss}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md transition-colors"
          style={{
            background: "var(--bg-input)",
            border: "1px solid var(--border-input)",
            color: "var(--text-secondary)",
          }}
          title="Discuss in chat"
        >
          <MessageSquare className="w-3 h-3" />
          Discuss
        </button>
        <button
          onClick={onReject}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md transition-colors"
          style={{
            background: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.4)",
            color: "#ef4444",
          }}
          title="Dismiss permanently — Gemini won't suggest this again"
        >
          <ThumbsDown className="w-3 h-3" />
          Reject
        </button>
      </div>
    </div>
  );
}
